import { useCallback } from 'react';

import type { Region } from '@sg/garnish';
import { useDebounceFn } from '@sg/garnish';

import { type CompleteAddressForm } from '../../components';
import { getLocationOrderChannel } from '../../helpers';

import type {
  AddressDetails,
  AddressWithoutIds,
  Location,
  LocationNavigationEntryPoint,
  LocationSearchService,
} from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Event-sender helpers bound to a `LocationSearchService` actor ref.
 */
export const useLocationSearchHelpers = (params: Params) => {
  const { locationSearchActorRef } = params;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setLocationSearchType = useCallback(
    (searchType: string) => {
      if (searchType === 'pickup') {
        locationSearchActorRef.send({ type: 'SET_LOCATION_SEARCH_TYPE_PICKUP' });
      }

      if (searchType === 'delivery') {
        locationSearchActorRef.send({ type: 'SET_LOCATION_SEARCH_TYPE_DELIVERY' });
      }

      if (searchType === 'outpost') {
        locationSearchActorRef.send({ type: 'SET_LOCATION_SEARCH_TYPE_OUTPOST' });
      }
    },
    [locationSearchActorRef],
  );

  const searchBasedOnCurrentPosition = useCallback(() => {
    locationSearchActorRef.send({ type: 'SEARCH_BY_CURRENT_POSITION' });
  }, [locationSearchActorRef]);

  const searchLocationsByArea = useCallback(() => {
    locationSearchActorRef.send({ type: 'SEARCH_BY_AREA' });
  }, [locationSearchActorRef]);

  const searchDeliveryAddresses = useDebounceFn(
    useCallback(
      (searchString: string) => {
        locationSearchActorRef.send({ type: 'SET_DELIVERY_SEARCH_STRING', searchString });
      },
      [locationSearchActorRef],
    ),
    200,
  );

  const setLocationSearchArea = useCallback(
    (region: Region) => {
      const { topLeft, bottomRight } = region;

      if (!topLeft || !bottomRight) return;

      locationSearchActorRef.send({
        type: 'SET_SEARCH_AREA',
        searchArea: { topLeft, bottomRight },
      });
    },
    [locationSearchActorRef],
  );

  const setDeliveryPredictionPlaceId = useCallback(
    (placeId: string, placeAddress: string) => {
      locationSearchActorRef.send({ type: 'SET_DELIVERY_PREDICTION_PLACE_ID', placeId, placeAddress }); // prettier-ignore
    },
    [locationSearchActorRef],
  );

  const setDeliveryPredictionDetails = useCallback(
    (predictedAddress: AddressWithoutIds) => {
      locationSearchActorRef.send({ type: 'SET_DELIVERY_PREDICTION_DETAILS', predictedAddress });
    },
    [locationSearchActorRef],
  );

  const setDeliveryCustomerAddress = useCallback(
    (addressDetails: AddressDetails) => {
      locationSearchActorRef.send({ type: 'SET_DELIVERY_CUSTOMER_ADDRESS', addressDetails });
    },
    [locationSearchActorRef],
  );

  const setFocusedLocation = useCallback(
    (id?: string) => {
      locationSearchActorRef.send({ type: 'SET_FOCUSED_PIN_ID', id });
      locationSearchActorRef.send({ type: 'SET_FOCUSED_CARD_ID', id });
    },
    [locationSearchActorRef],
  );

  const setFocusedLocationPin = useCallback(
    (id: string | undefined) => {
      locationSearchActorRef.send({ type: 'SET_FOCUSED_PIN_ID', id });
    },
    [locationSearchActorRef],
  );

  const confirmLocationWarning = useCallback(() => {
    locationSearchActorRef.send({ type: 'LOCATION_WARNING_CONFIRM' });
  }, [locationSearchActorRef]);

  const cancelLocationWarning = useCallback(() => {
    locationSearchActorRef.send({ type: 'LOCATION_WARNING_CANCEL' });
  }, [locationSearchActorRef]);

  const navigateToSelectedLocation = useCallback(
    (params: LocationRestaurantData, options?: LocationNavigationOptions) => {
      const { restaurantSlug } = params;

      locationSearchActorRef.send({
        type: 'NAVIGATE_TO_LOCATION',
        restaurantSlug: restaurantSlug ?? '',
        entryPoint: options?.entryPoint,
      });
    },
    [locationSearchActorRef],
  );

  const cancelDeliveryAddressForm = useCallback(() => {
    locationSearchActorRef.send({ type: 'CANCEL_DELIVERY_ADDRESS_FORM' });
  }, [locationSearchActorRef]);

  const submitDeliveryAddressForm = useCallback(
    (address: CompleteAddressForm) => {
      const { deliveryLocation } = locationSearchActorRef.getSnapshot().context;

      if (!deliveryLocation) return;

      locationSearchActorRef.send({
        type: 'SUBMIT_DELIVERY_ADDRESS_FORM',
        address: {
          ...deliveryLocation,

          // new parameters that were submitted from the form.
          secondaryStreet: address.secondaryStreet,
          name: address.name,
          notes: address.notes,
          deliveryPreference: address.deliveryPreference,
        },
      });
    },
    [locationSearchActorRef],
  );

  const navigateToSelectedDeliveryLocation = useCallback(
    (_location: LocationRestaurantData, options?: LocationNavigationOptions) => {
      locationSearchActorRef.send({
        type: 'NAVIGATE_TO_DELIVERY_LOCATION',
        entryPoint: options?.entryPoint,
      });
    },
    [locationSearchActorRef],
  );

  const navigateToRecentOrNearbyLocation = useCallback(
    (params: NavigateToLocationParams) => {
      const { location, options } = params;

      const locationOrderChannel = getLocationOrderChannel(location);

      if (locationOrderChannel === 'pickup') {
        locationSearchActorRef.send({
          type: 'NAVIGATE_TO_RECENT_PICKUP_LOCATION',
          restaurantSlug: location.restaurantSlug,
          entryPoint: options?.entryPoint,
        });

        return;
      }

      if (locationOrderChannel === 'outpost') {
        locationSearchActorRef.send({
          type: 'NAVIGATE_TO_RECENT_OUTPOST_LOCATION',
          restaurantSlug: location.restaurantSlug,
          entryPoint: options?.entryPoint,
        });

        return;
      }

      locationSearchActorRef.send({
        type: 'NAVIGATE_TO_RECENT_DELIVERY_LOCATION',
        addressId: location.addressId ?? '',
        entryPoint: options?.entryPoint,
      });
    },
    [locationSearchActorRef],
  );

  const showRecentAndNearbyLocations = useCallback(() => {
    locationSearchActorRef.send({ type: 'SHOW_RECENT_AND_NEARBY_LOCATIONS' });
  }, [locationSearchActorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    setLocationSearchType,
    searchBasedOnCurrentPosition,
    searchLocationsByArea,
    searchDeliveryAddresses,
    setLocationSearchArea,
    setDeliveryPredictionPlaceId,
    setDeliveryPredictionDetails,
    setDeliveryCustomerAddress,
    setFocusedLocation,
    setFocusedLocationPin,
    confirmLocationWarning,
    cancelLocationWarning,
    navigateToSelectedLocation,
    cancelDeliveryAddressForm,
    submitDeliveryAddressForm,
    navigateToSelectedDeliveryLocation,
    navigateToRecentOrNearbyLocation,
    showRecentAndNearbyLocations,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  locationSearchActorRef: LocationSearchService;
};

type LocationRestaurantData = { restaurantSlug?: string };

type NavigateToLocationParams = {
  location: Location;
  options?: LocationNavigationOptions;
};

type LocationNavigationOptions = {
  entryPoint?: LocationNavigationEntryPoint;
};
